import { useEffect, useMemo, useRef, useState } from 'react'
import { Circle, GoogleMap, Marker, Polygon } from '@react-google-maps/api'
import { Minus, Plus } from 'lucide-react'

import { Button } from '@/components/ui/button'

const TILE = 256
const MIN_ZOOM = 3
const MAX_ZOOM = 18

export type SiteMapPoint = { lat: number; lng: number }

export type SiteMapMarker = {
  id: string
  position: SiteMapPoint
  label: string
  polygon?: [number, number][]
  radiusM?: number | null
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const project = (position: SiteMapPoint, zoom: number) => {
  const sinLat = clamp(Math.sin((position.lat * Math.PI) / 180), -0.9999, 0.9999)
  const scale = TILE * 2 ** zoom
  return {
    x: ((position.lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale,
  }
}

const unproject = (point: { x: number; y: number }, zoom: number): SiteMapPoint => {
  const scale = TILE * 2 ** zoom
  const lng = (point.x / scale) * 360 - 180
  const mercator = Math.PI - (2 * Math.PI * point.y) / scale
  const lat = (180 / Math.PI) * Math.atan(Math.sinh(mercator))
  return { lat, lng }
}

const fitViewport = (points: SiteMapPoint[], width: number, height: number, fallback: SiteMapPoint) => {
  if (points.length === 0) return { zoom: 12, center: fallback }
  if (points.length === 1) return { zoom: 14, center: points[0] }

  for (let zoom = MAX_ZOOM; zoom >= MIN_ZOOM; zoom -= 1) {
    const projected = points.map((point) => project(point, zoom))
    const xs = projected.map((point) => point.x)
    const ys = projected.map((point) => point.y)
    const spanX = Math.max(...xs) - Math.min(...xs)
    const spanY = Math.max(...ys) - Math.min(...ys)
    if (spanX <= Math.max(width - 96, 120) && spanY <= Math.max(height - 96, 120)) {
      return {
        zoom,
        center: unproject({
          x: (Math.min(...xs) + Math.max(...xs)) / 2,
          y: (Math.min(...ys) + Math.max(...ys)) / 2,
        }, zoom),
      }
    }
  }

  const average = {
    lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
    lng: points.reduce((sum, point) => sum + point.lng, 0) / points.length,
  }
  return { zoom: MIN_ZOOM, center: average }
}

function OsmSitesMap({
  markers,
  height,
  defaultCenter,
  selectedId,
  onSelect,
  onMapClick,
}: {
  markers: SiteMapMarker[]
  height: number
  defaultCenter: SiteMapPoint
  selectedId?: string | null
  onSelect?: (id: string) => void
  onMapClick?: (lat: number, lng: number) => void
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const dragStartRef = useRef<{ pointerId: number; x: number; y: number; panX: number; panY: number } | null>(null)
  const dragMovedRef = useRef(false)
  const [width, setWidth] = useState(800)
  const [zoomOffset, setZoomOffset] = useState(0)
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const element = containerRef.current
    if (!element) return
    const update = () => {
      const next = element.getBoundingClientRect().width
      if (next > 0) setWidth(next)
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const markerKey = markers.map((marker) => `${marker.id}:${marker.position.lat}:${marker.position.lng}`).join('|')
  const fitted = useMemo(() => {
    const selected = markers.find((marker) => marker.id === selectedId)
    if (selected) return { zoom: 13, center: selected.position }
    return fitViewport(markers.map((marker) => marker.position), width, height, defaultCenter)
  }, [defaultCenter, height, markerKey, markers, selectedId, width])

  useEffect(() => {
    setZoomOffset(0)
    setPanOffset({ x: 0, y: 0 })
  }, [markerKey, selectedId])

  const zoom = clamp(fitted.zoom + zoomOffset, MIN_ZOOM, MAX_ZOOM)
  const centerPoint = project(fitted.center, zoom)
  const visibleCenter = {
    x: centerPoint.x - panOffset.x,
    y: centerPoint.y - panOffset.y,
  }
  const tilesPerAxis = 2 ** zoom
  const minTileX = Math.floor((visibleCenter.x - width / 2) / TILE)
  const maxTileX = Math.floor((visibleCenter.x + width / 2) / TILE)
  const minTileY = Math.floor((visibleCenter.y - height / 2) / TILE)
  const maxTileY = Math.floor((visibleCenter.y + height / 2) / TILE)
  const tiles = []
  for (let x = minTileX; x <= maxTileX; x += 1) {
    for (let y = minTileY; y <= maxTileY; y += 1) {
      if (y < 0 || y >= tilesPerAxis) continue
      const wrappedX = ((x % tilesPerAxis) + tilesPerAxis) % tilesPerAxis
      tiles.push({
        key: `${zoom}-${x}-${y}`,
        url: `https://tile.openstreetmap.org/${zoom}/${wrappedX}/${y}.png`,
        left: x * TILE - visibleCenter.x + width / 2,
        top: y * TILE - visibleCenter.y + height / 2,
      })
    }
  }

  const toScreen = (position: SiteMapPoint) => {
    const point = project(position, zoom)
    return {
      x: point.x - visibleCenter.x + width / 2,
      y: point.y - visibleCenter.y + height / 2,
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden bg-slate-100"
      style={{ height }}
      role="application"
      aria-label="Locations map"
      onPointerDown={(event) => {
        if (event.button !== 0) return
        event.currentTarget.setPointerCapture(event.pointerId)
        dragMovedRef.current = false
        dragStartRef.current = {
          pointerId: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          panX: panOffset.x,
          panY: panOffset.y,
        }
      }}
      onPointerMove={(event) => {
        const dragStart = dragStartRef.current
        if (!dragStart || dragStart.pointerId !== event.pointerId) return
        const deltaX = event.clientX - dragStart.x
        const deltaY = event.clientY - dragStart.y
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) dragMovedRef.current = true
        setPanOffset({ x: dragStart.panX + deltaX, y: dragStart.panY + deltaY })
      }}
      onPointerUp={(event) => {
        if (dragStartRef.current?.pointerId === event.pointerId) {
          dragStartRef.current = null
        }
      }}
      onClick={(event) => {
        if (dragMovedRef.current) {
          dragMovedRef.current = false
          return
        }
        if (!onMapClick) return
        const rect = event.currentTarget.getBoundingClientRect()
        const world = {
          x: visibleCenter.x + event.clientX - rect.left - width / 2,
          y: visibleCenter.y + event.clientY - rect.top - height / 2,
        }
        const next = unproject(world, zoom)
        onMapClick(next.lat, next.lng)
      }}
    >
      {tiles.map((tile) => (
        <img
          key={tile.key}
          src={tile.url}
          alt=""
          draggable={false}
          className="pointer-events-none absolute h-64 w-64 max-w-none"
          style={{ left: tile.left, top: tile.top }}
        />
      ))}
      <svg className="pointer-events-none absolute inset-0 h-full w-full">
        {markers.map((marker) => {
          if (marker.polygon && marker.polygon.length >= 3) {
            const points = marker.polygon
              .map(([lat, lng]) => toScreen({ lat, lng }))
              .map((point) => `${point.x},${point.y}`)
              .join(' ')
            return <polygon key={`poly-${marker.id}`} points={points} fill="#0f7cf3" fillOpacity={0.16} stroke="#0f7cf3" strokeWidth={2} />
          }
          if (marker.radiusM && marker.radiusM > 0) {
            const center = toScreen(marker.position)
            const north = toScreen({
              lat: marker.position.lat + (marker.radiusM / 6378137) * (180 / Math.PI),
              lng: marker.position.lng,
            })
            const radius = Math.max(Math.abs(center.y - north.y), 8)
            return <circle key={`radius-${marker.id}`} cx={center.x} cy={center.y} r={radius} fill="#0f7cf3" fillOpacity={0.14} stroke="#0f7cf3" strokeWidth={2} />
          }
          return null
        })}
      </svg>
      {markers.map((marker) => {
        const point = toScreen(marker.position)
        const selected = selectedId === marker.id
        return (
          <button
            key={marker.id}
            type="button"
            title={marker.label}
            className={`absolute z-10 -translate-x-1/2 -translate-y-full rounded-full border-2 border-white shadow ${selected ? 'h-5 w-5 bg-blue-700' : 'h-4 w-4 bg-blue-500'}`}
            style={{ left: point.x, top: point.y }}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation()
              onSelect?.(marker.id)
            }}
          />
        )
      })}
      <div className="absolute bottom-2 right-2 z-10 flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <Button type="button" size="icon" variant="ghost" className="h-8 w-8 rounded-none" aria-label="Zoom in" onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); setZoomOffset((value) => value + 1) }}>
          <Plus className="h-4 w-4" />
        </Button>
        <Button type="button" size="icon" variant="ghost" className="h-8 w-8 rounded-none border-t" aria-label="Zoom out" onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); setZoomOffset((value) => value - 1) }}>
          <Minus className="h-4 w-4" />
        </Button>
      </div>
      <a
        href="https://www.openstreetmap.org/copyright"
        target="_blank"
        rel="noreferrer"
        className="absolute bottom-2 left-2 z-10 rounded bg-white/90 px-1.5 py-0.5 text-[10px] text-slate-500"
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        © OpenStreetMap
      </a>
    </div>
  )
}

export function SitesMap({
  markers,
  mapsReady,
  height = 520,
  defaultCenter,
  selectedId,
  onSelect,
  onMapClick,
}: {
  markers: SiteMapMarker[]
  mapsReady: boolean
  height?: number
  defaultCenter: SiteMapPoint
  selectedId?: string | null
  onSelect?: (id: string) => void
  onMapClick?: (lat: number, lng: number) => void
}) {
  const center = markers.find((marker) => marker.id === selectedId)?.position
    ?? markers[0]?.position
    ?? defaultCenter

  if (!mapsReady) {
    return (
      <OsmSitesMap
        markers={markers}
        height={height}
        defaultCenter={defaultCenter}
        selectedId={selectedId}
        onSelect={onSelect}
        onMapClick={onMapClick}
      />
    )
  }

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height }}
      center={center}
      zoom={markers.length > 1 ? 11 : 14}
      onLoad={(map) => {
        if (markers.length < 2 || !window.google?.maps) return
        const bounds = new window.google.maps.LatLngBounds()
        markers.forEach((marker) => bounds.extend(marker.position))
        map.fitBounds(bounds, 48)
      }}
      onClick={(event) => {
        const lat = event.latLng?.lat()
        const lng = event.latLng?.lng()
        if (lat != null && lng != null) onMapClick?.(lat, lng)
      }}
      options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: false }}
    >
      {markers.map((marker) => (
        <Marker
          key={marker.id}
          position={marker.position}
          title={marker.label}
          onClick={() => onSelect?.(marker.id)}
        />
      ))}
      {markers.map((marker) => (
        marker.polygon && marker.polygon.length >= 3 ? (
          <Polygon
            key={`poly-${marker.id}`}
            path={marker.polygon.map(([lat, lng]) => ({ lat, lng }))}
            options={{ strokeColor: '#0f7cf3', fillColor: '#0f7cf3', fillOpacity: 0.16, strokeWeight: 2 }}
          />
        ) : marker.radiusM && marker.radiusM > 0 ? (
          <Circle
            key={`radius-${marker.id}`}
            center={marker.position}
            radius={marker.radiusM}
            options={{ strokeColor: '#0f7cf3', fillColor: '#0f7cf3', fillOpacity: 0.12, strokeWeight: 2 }}
          />
        ) : null
      ))}
    </GoogleMap>
  )
}
